import './DevPanel_ApolloStateTreeView.scss';

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography
} from '@mui/material';
import { RichTreeView } from '@mui/x-tree-view';

import { useAppConfigContext } from '@ulta/core/providers/AppConfigProvider/AppConfigProvider';

import { useDevPanelSocket } from '../../providers/DevPanel_SocketProvider/DevPanel_SocketProvider';
import {
  crawlForModules,
  extractModuleTreesFromPages,
  findNodeByContentId,
  findNodeById
} from '../../utils/treeViewUtils/treeViewUtils';

const useNodeId = () => {
  const counterRef = useRef( 0 );

  return useCallback(
    () => `node-${++counterRef.current}`,
    []
  );
};

export const MultilineLabel = ( props ) => {
  const { children: label } = props;

  const safeLabel =
    typeof label === 'string' ? label : '';

  const [title, subtitle] =
    safeLabel.split( '::' );

  return (
    <Box className='multiline-label-container'>
      <Typography className='label-title'>
        { title }

        { subtitle && (
          <Typography
            component='span'
            display='inline'
            className='label-subtitle'
          >
            { title && ' ↠ ' }
            { subtitle }
          </Typography>
        ) }
      </Typography>
    </Box>
  );
};

const DevPanel_ApolloStateTreeView = ( {
  apolloState,
  setSelectedNode
} ) => {
  const getNodeId = useNodeId();
  const socket = useDevPanelSocket();

  const [searchTerm, setSearchTerm] =
    useState( '' );

  const [notification, setNotification] =
    useState( null );

  const { serverRouteUtility, isLocal } =
    useAppConfigContext();

  const { content } = useMemo(
    () =>
      extractModuleTreesFromPages(
        apolloState
      ),
    [apolloState]
  );

  const richTreeViewData =
    useMemo(
      () => {
        if(
          !content ||
          !Array.isArray(
            content.modules
          )
        ){
          return [];
        }

        return crawlForModules(
          content,
          getNodeId
        );
      },
      [content, getNodeId]
    );

  useEffect( () => {
    if( !socket ){
      return undefined;
    }

    const handleContentUpdated = ( data ) => {
      const contentId = data?.contentId;
      const targetNode = findNodeByContentId( richTreeViewData, contentId );

      if( targetNode ){
        setNotification( {
          message:
              data?.message ||
              'Content updated',
          contentId:
              contentId ||
              'N/A',
          moduleName:
              data?.moduleName ||
              'N/A',
          severity: 'info'
        } );
      }
    };

    socket.on( 'content-updated', handleContentUpdated );

    return () => {
      socket.off( 'content-updated', handleContentUpdated );
    };
  }, [socket, richTreeViewData] );

  const filteredTreeViewData =
    useMemo( () => {
      if( !searchTerm ){
        return richTreeViewData;
      }

      const term =
        searchTerm.toLowerCase();

      const filterNodes = (
        nodes
      ) => {
        return nodes.reduce(
          ( acc, node ) => {
            const isMatch =
              node.label
                .toLowerCase()
                .includes( term );

            const filteredChildren =
              filterNodes(
                node.children || []
              );

            if(
              isMatch ||
              filteredChildren.length > 0
            ){
              acc.push( {
                ...node,
                children:
                  filteredChildren
              } );
            }

            return acc;
          },
          []
        );
      };

      return filterNodes(
        richTreeViewData
      );
    }, [
      richTreeViewData,
      searchTerm
    ] );

  const nodeSelectHandler =
    useCallback(
      ( event, itemId ) => {
        const selectedItem =
          findNodeById(
            richTreeViewData,
            itemId
          );

        if(
          selectedItem &&
          setSelectedNode
        ){
          setSelectedNode( {
            ...selectedItem.moduleData,
            disableAmplienceLink:
              selectedItem.disableAmplienceLink
          } );
        }

        const targetEl =
          document.querySelector(
            `[data-debug-id$="${itemId}"]`
          );

        if( targetEl ){
          targetEl.scrollIntoView(
            {
              behavior: 'smooth',
              block: 'center'
            }
          );
        }
      },
      [
        richTreeViewData,
        setSelectedNode
      ]
    );

  const handleNotificationClick =
    useCallback(
      ( contentId ) => {
        const targetNode =
          findNodeByContentId(
            richTreeViewData,
            contentId
          );

        if( targetNode ){
          nodeSelectHandler(
            null,
            targetNode.id
          );

          setNotification(
            null
          );
        }
      },
      [
        richTreeViewData,
        nodeSelectHandler
      ]
    );

  if(
    richTreeViewData.length === 0
  ){
    return (
      <Box p={ 2 }>
        <Typography
          variant='body2'
          color='textSecondary'
        >
          No modules found in Apollo
          state.
        </Typography>
      </Box>
    );
  }

  return (
    <div className='DevPanel_ApolloStateTreeView'>
      { notification && (
        <Box mb={ 2 } px={ 2 } pt={ 2 }>
          <Alert
            severity={
              notification.severity
            }
            onClose={ () =>
              setNotification(
                null
              )
            }
          >
            Real-time Update:{ ' ' }
            {
              notification.message
            }{ ' ' }
            (ID:{ ' ' }
            {
              notification.contentId
            }
            ).{ ' ' }

            { notification.moduleName && (
              <Link
                component='button'
                variant='body2'
                onClick={ () =>
                  handleNotificationClick(
                    notification.contentId
                  )
                }
              >
                View{ ' ' }
                {
                  notification.moduleName
                }
              </Link>
            ) }
          </Alert>
        </Box>
      ) }

      <Box className='search-container'>
        <TextField
          fullWidth
          size='small'
          placeholder='Search page data...'
          value={ searchTerm }
          onChange={ ( e ) =>
            setSearchTerm(
              e.target.value
            )
          }
          variant='outlined'
          InputProps={ {
            endAdornment:
              searchTerm && (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='clear search'
                    onClick={ () =>
                      setSearchTerm(
                        ''
                      )
                    }
                    edge='end'
                    size='small'
                  >
                    <ClearIcon fontSize='small' />
                  </IconButton>
                </InputAdornment>
              )
          } }
        />
      </Box>

      <RichTreeView
        aria-label='apollo module tree'
        items={
          filteredTreeViewData
        }
        getItemId={ ( item ) =>
          item.id
        }
        getItemLabel={ ( item ) =>
          item.label
        }
        getItemChildren={ ( item ) =>
          item.children
        }
        slotProps={ {
          item: {
            slots: {
              label:
                MultilineLabel
            }
          }
        } }
        onItemClick={
          nodeSelectHandler
        }
      />
    </div>
  );
};

export default DevPanel_ApolloStateTreeView;
